import React, { useState, useEffect } from 'react'
import { rankEntries, rankLabel } from './leaderboardRanks'
import { useNavigate } from 'react-router-dom'
import { cardStyle, accent, bgMuted, textPrimary, sectionLabelStyle, statValueStyle, borderDefault } from './styles'
import { getTodayString, getApiUrl, getHomeUrl, isSunday } from './gameLogic'
import Leaderboard from './Leaderboard'
import PlayerGameModal from './PlayerGameModal'
import { LeaderboardFrame, DateSelector, formatLeaderboardDate, fetchGameStatuses } from '@precisionpros/game-ui'
import { eventProGame } from './EventProBrand'

const getOtherGameFrontends = () => {
  const apiUrl = getApiUrl()
  const isLocalDev = apiUrl.includes("localhost") || apiUrl.includes("127.0.0.1")
  if (isLocalDev) {
    return {
      wordpro: "http://localhost:3005",
      connectpro: "http://localhost:3001",
      buildpro: "http://localhost:3003",
      wordprofinder: "http://localhost:3004",
      seashroud: "http://localhost:3006",
      mathpro: "http://localhost:3008",
    }
  }
  return {
    wordpro: "https://wordpro.precisionpros.com",
    connectpro: "https://connectpro.precisionpros.com",
    buildpro: "https://buildpro.precisionpros.com",
    wordprofinder: "https://wordprofinder.precisionpros.com",
    seashroud: "https://seashroud.precisionpros.com",
    mathpro: "https://mathpro.precisionpros.com",
  }
}

const getOtherGameApis = () => {
  const apiUrl = getApiUrl()
  const isLocalDev = apiUrl.includes("localhost") || apiUrl.includes("127.0.0.1")
  if (isLocalDev) {
    return {
      wordpro: "http://localhost:8006",
      connectpro: "http://localhost:8002",
      buildpro: "http://localhost:8004",
      wordprofinder: "http://localhost:8005/api/wordprofinder",
    }
  }
  return {
    wordpro: "https://wordpro.precisionpros.com",
    connectpro: "https://connectpro.precisionpros.com",
    buildpro: "https://buildpro.precisionpros.com",
    wordprofinder: "https://wordprofinder.precisionpros.com/api/wordprofinder",
  }
}

function LeaderboardPage({ playerName }) {
  const navigate = useNavigate()
  const currentPlayerName = playerName || new URLSearchParams(window.location.search).get('player_name') || localStorage.getItem('playerName') || ''
  const [date, setDate] = useState(() => getTodayString())
  const [leaderboard, setLeaderboard] = useState([])
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([])
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [isPrePlay, setIsPrePlay] = useState(null)
  const [preSundayStandings, setPreSundayStandings] = useState([])
  const [otherGamesStatus, setOtherGamesStatus] = useState({})
  const [checkingOtherGames, setCheckingOtherGames] = useState(false)
  const [statusReady, setStatusReady] = useState(false)

  const apiBaseUrl = getApiUrl()

  const frontends = getOtherGameFrontends()
  const links = [
    { key: 'wordpro', letter: 'W', color: '#4CAF50', href: `${frontends.wordpro}${otherGamesStatus.wordpro ? '/leaderboard' : '/'}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ''}`, played: !!otherGamesStatus.wordpro },
    { key: 'connectpro', letter: 'C', color: '#2563eb', href: `${frontends.connectpro}${otherGamesStatus.connectpro ? '/leaderboard' : '/'}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ''}`, played: !!otherGamesStatus.connectpro },
    { key: 'eventpro', letter: 'E', color: '#ea580c', href: `${otherGamesStatus.eventpro ? '/leaderboard' : '/'}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ''}`, played: !!otherGamesStatus.eventpro },
    { key: 'buildpro', letter: 'B', color: '#0f766e', href: `${frontends.buildpro}${otherGamesStatus.buildpro ? '/leaderboard' : '/'}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ''}`, played: !!otherGamesStatus.buildpro },
    { key: 'wordprofinder', letter: 'WF', color: '#4CAF50', href: `${frontends.wordprofinder}${otherGamesStatus.wordprofinder ? '/leaderboard' : '/'}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ''}`, played: !!otherGamesStatus.wordprofinder },
    { key: 'seashroud', letter: 'S', color: '#287f89', href: `${frontends.seashroud}${otherGamesStatus.seashroud ? '/leaderboard' : '/'}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ''}`, played: !!otherGamesStatus.seashroud },
    { key: 'mathpro', letter: 'M', color: '#996128', href: `${frontends.mathpro}${otherGamesStatus.mathpro ? '/leaderboard' : '/'}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ''}`, played: !!otherGamesStatus.mathpro },
  ]
  const displayedLinks = statusReady ? links : []

  useEffect(() => {
    const hasHubToken = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken');
    if (!currentPlayerName && !hasHubToken) {
      setOtherGamesStatus({})
      setCheckingOtherGames(false)
      setStatusReady(true)
      return
    }

    let cancelled = false
    setCheckingOtherGames(true)
    setStatusReady(false)
    setOtherGamesStatus({})
    fetchGameStatuses().then((statuses) => {
      if (cancelled) return
      setOtherGamesStatus(statuses)
      setCheckingOtherGames(false)
    }).catch(() => {
      if (!cancelled) { setOtherGamesStatus({}); setCheckingOtherGames(false) }
    }).finally(() => {
      if (!cancelled) setStatusReady(true)
    })

    return () => {
      cancelled = true
    }
  }, [currentPlayerName])

  useEffect(() => {
    fetchData()
  }, [date, statusReady])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      // Check if current player has played today (any day of the week)
      const todayStr = getTodayString()
      const viewingToday = date === todayStr
      let playedToday = false
      let fetchedTodayLeaderboard = null

      const hasHubToken = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken')
      if (viewingToday && !statusReady) {
        return
      }
      if (viewingToday && (currentPlayerName || hasHubToken)) {
        playedToday = otherGamesStatus.eventpro === true
      }

      // The Hub status adapter is authoritative when available, but older
      // direct/legacy sessions may have a player name without a Hub token.
      // In that case, use the player's presence in today's public results as
      // a fallback so completed players are not shown the pre-play screen.
      if (viewingToday && currentPlayerName && !playedToday) {
        const todayLeaderboardRes = await fetch(`${apiBaseUrl}/eventpro/leaderboard/?date=${date}`)
        if (todayLeaderboardRes.ok) {
          fetchedTodayLeaderboard = await todayLeaderboardRes.json()
          const normalizedPlayer = currentPlayerName.trim().toLowerCase()
          playedToday = Array.isArray(fetchedTodayLeaderboard)
            && fetchedTodayLeaderboard.some((entry) => String(entry.player_name || '').trim().toLowerCase() === normalizedPlayer)
        }
      }

      const prePlay = viewingToday && !playedToday

      setIsPrePlay(prePlay)

      if (prePlay) {
        // Player hasn't played today — hide daily results and all-time leaderboard
        setLeaderboard([])
        setAllTimeLeaderboard([])

        // Sunday: show pre-Sunday standings
        if (isSunday()) {
          const preSundayRes = await fetch(`${apiBaseUrl}/eventpro/pre-sunday-leaderboard/`)
          if (preSundayRes.ok) {
            setPreSundayStandings(await preSundayRes.json())
          }
          setWeeklyLeaderboard([])
        } else {
          // Non-Sunday: show yesterday's weekly leaderboard
          const d = new Date(date)
          d.setDate(d.getDate() - 1)
          const yesterdayStr = d.toISOString().split('T')[0]

          const statsRes = await fetch(`${apiBaseUrl}/eventpro/leaderboard-stats/?date=${yesterdayStr}`)
          if (statsRes.ok) {
            const data = await statsRes.json()
            setWeeklyLeaderboard(data.weekly_leaderboard || [])
          }
          setPreSundayStandings([])
        }
      } else {
        // Player has played or it's not today — show everything
        // Fetch daily leaderboard
        if (fetchedTodayLeaderboard) {
          setLeaderboard(fetchedTodayLeaderboard)
        } else {
          const leaderboardRes = await fetch(`${apiBaseUrl}/eventpro/leaderboard/?date=${date}`)
          if (!leaderboardRes.ok) throw new Error(`Daily leaderboard request failed: ${leaderboardRes.status}`)
          setLeaderboard(await leaderboardRes.json())
        }

        // Fetch weekly and all-time leaderboards
        const statsRes = await fetch(`${apiBaseUrl}/eventpro/leaderboard-stats/?date=${date}`)
        if (!statsRes.ok) throw new Error(`Leaderboard stats request failed: ${statsRes.status}`)
        const data = await statsRes.json()
        setWeeklyLeaderboard(data.weekly_leaderboard || [])
        setAllTimeLeaderboard(data.all_time_leaderboard || [])
        setPreSundayStandings([])
      }
    } catch (err) {
      setError('Failed to load leaderboard')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handlePrevDay = () => {
    const d = new Date(date)
    d.setDate(d.getDate() - 1)
    setDate(d.toISOString().split('T')[0])
  }

  const handleNextDay = () => {
    const d = new Date(date)
    d.setDate(d.getDate() + 1)
    setDate(d.toISOString().split('T')[0])
  }

  const WeeklyTable = ({ data, currentPlayer }) => {
    if (!data || data.length === 0) {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No entries yet</div>
    }

    return (
      <div>
        <style>{`
          @media (max-width: 640px) {
            .weekly-table-yesterday-col {
              display: none;
            }
          }
        `}</style>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.9rem',
          }}
        >
          <thead>
            <tr style={{ borderBottom: `2px solid ${borderDefault}` }}>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#999', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Rank
              </th>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#999', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Name
              </th>
              <th className="weekly-table-yesterday-col" style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', color: '#999', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Yesterday's Bank
              </th>
              <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', color: '#999', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Banked Today
              </th>
              <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', color: '#999', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Weekly Bank
              </th>
            </tr>
          </thead>
        <tbody>
          {rankEntries(data, 'weekly_bank').map((entry, idx) => {
            const isCurrentPlayer = entry.player_name === currentPlayer
            const yesterdaysBank = parseFloat(entry.yesterdays_bank || 0)
            const isBankedTodayDNP = entry.banked_today === "DNP"
            const bankedToday = isBankedTodayDNP ? 0 : parseFloat(entry.banked_today || 0)
            const weeklyBank = parseFloat(entry.weekly_bank || 0)
            return (
              <tr
                key={idx}
                style={{
                  borderBottom: `1px solid ${borderDefault}`,
                  backgroundColor: isCurrentPlayer ? '#f0faf0' : 'transparent',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isCurrentPlayer ? '#e0f5e0' : '#f5f5f5'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isCurrentPlayer ? '#f0faf0' : 'transparent'
                }}
              >
                <td aria-label={`${entry.tied ? 'Tied for rank' : 'Rank'} ${entry.rank}`} style={{ padding: '12px 8px', color: textPrimary, fontWeight: '600' }}>
                  {rankLabel(entry)}
                </td>
                <td style={{ padding: '12px 8px', color: textPrimary }}>
                  <span>{entry.player_name}</span>
                  {isCurrentPlayer && (
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: accent,
                      }}
                    >
                      you
                    </span>
                  )}
                </td>
                <td className="weekly-table-yesterday-col" style={{ padding: '12px 8px', textAlign: 'right', color: '#666', fontWeight: '600' }}>
                  ${yesterdaysBank.toFixed(2)}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'right', color: isBankedTodayDNP ? '#999' : '#15803d', fontWeight: '600' }}>
                  {isBankedTodayDNP ? 'DNP' : `$${bankedToday.toFixed(2)}`}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'right', color: '#15803d', fontWeight: '600' }}>
                  ${weeklyBank.toFixed(2)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>
    )
  }

  const AllTimeTable = ({ data, currentPlayer }) => {
    if (!data || data.length === 0) {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No entries yet</div>
    }

    return (
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem',
        }}
      >
        <thead>
          <tr style={{ borderBottom: `2px solid ${borderDefault}` }}>
            <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#999', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Rank
            </th>
            <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#999', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Name
            </th>
            <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', color: '#999', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Best Bank
            </th>
          </tr>
        </thead>
        <tbody>
          {rankEntries(data, 'best_bank').map((entry, idx) => {
            const isCurrentPlayer = entry.player_name === currentPlayer
            return (
              <tr
                key={idx}
                style={{
                  borderBottom: `1px solid ${borderDefault}`,
                  backgroundColor: isCurrentPlayer ? '#f0faf0' : 'transparent',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isCurrentPlayer ? '#e0f5e0' : '#f5f5f5'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isCurrentPlayer ? '#f0faf0' : 'transparent'
                }}
              >
                <td aria-label={`${entry.tied ? 'Tied for rank' : 'Rank'} ${entry.rank}`} style={{ padding: '12px 8px', color: textPrimary, fontWeight: '600' }}>
                  {rankLabel(entry)}
                </td>
                <td style={{ padding: '12px 8px', color: textPrimary }}>
                  <span>{entry.player_name}</span>
                  {isCurrentPlayer && (
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: accent,
                      }}
                    >
                      you
                    </span>
                  )}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'right', color: accent, fontWeight: '600' }}>
                  ${parseFloat(entry.best_bank || 0).toFixed(2)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  return (
      <LeaderboardFrame game={eventProGame} currentGameKey="eventpro" title="Leaderboard" links={displayedLinks} homeHref={getHomeUrl()} showPrePlayBanner={isPrePlay} playHref={`${window.location.origin}/`} howToPlayHref={`${window.location.origin}/how-to-play`} dateSelector={<DateSelector dateLabel={formatLeaderboardDate(date)} onPrevious={handlePrevDay} onNext={handleNextDay} />}>

        {/* Daily Leaderboard — hidden on pre-play */}
        {isPrePlay === false && (
          <div style={{ ...cardStyle }}>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Loading...</div>
            ) : error ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>{error}</div>
            ) : (
              <Leaderboard data={leaderboard} playerName={playerName} />
            )}
          </div>
        )}

        {/* Weekly Leaderboard */}
        <div style={{ ...cardStyle }}>
          {isPrePlay === true && !isSunday() ? (
            <div
              onClick={() => navigate('/')}
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#fff7ed',
                borderRadius: '8px',
                border: `1px solid ${accent}`,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ffedd5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff7ed'}
            >
              <div style={{ fontSize: '0.85rem', fontWeight: '700', color: accent, marginBottom: '4px' }}>
                📅 Yesterday's Standings
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666' }}>
                Play today to update your ranking
              </div>
            </div>
          ) : (
            <div style={sectionLabelStyle}>
              {isPrePlay === true && isSunday() ? 'Entering Sunday — Weekly Bank' : 'Weekly Rankings'}
            </div>
          )}
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Loading...</div>
          ) : isPrePlay === true && isSunday() ? (
            <WeeklyTable data={preSundayStandings} currentPlayer={playerName} />
          ) : (
            <WeeklyTable data={weeklyLeaderboard} currentPlayer={playerName} />
          )}
        </div>

        {/* All-Time Leaderboard (Top 3) — hidden on pre-play */}
        <div style={{ ...cardStyle }}>
            <div style={sectionLabelStyle}>All-Time Top 3</div>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Loading...</div>
            ) : (
              <AllTimeTable data={allTimeLeaderboard} currentPlayer={playerName} />
            )}
        </div>

      {/* Player Details Modal */}
      {selectedPlayer && (
        <PlayerGameModal
          playerName={selectedPlayer.player_name}
          date={selectedPlayer.date}
          strikes={selectedPlayer.strikes}
          accuracy={selectedPlayer.accuracy}
          wager={selectedPlayer.wager}
          payout={selectedPlayer.payout}
          weeklyBank={selectedPlayer.weekly_bank}
          placedEventIds={selectedPlayer.placed_event_ids || []}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </LeaderboardFrame>
  )
}

export default LeaderboardPage
